import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import gsap from 'gsap';
import { isMobile } from 'mobile-device-detect';

// --- Movement Constants ---
const PLAYER_SPEED = 0.14; // Base forward movement speed
const ROTATION_SPEED = 0.0675 * 0.65; // Base rotation speed
const SNAP_ROTATION_FACTOR = 0.0625 * 0.65; // Smoothing factor for turning
const SPEED_LERP_FACTOR = 0.01; // 🚀 Smoothing factor for acceleration/deceleration

export default class Player {
  // Constructor takes a scene and camera as parameters
  constructor(scene, camera) {
    this.scene = scene;
    this.camera = camera;

    this.onEvents = this.onEvents.bind(this);

    // Kinematic state
    // 🆕 NEW PROPERTIES ADDED FOR COLLISION DETECTION
    this.raycaster = new THREE.Raycaster();
    this.collisionDistance = 1.5; // How far ahead to check for collision (adjust this value)

    this.input = { forward: false, left: false, right: false };
    this.targetRotationY = 0;
    this.currentSpeed = 0; // 🚀 Variable for smoothed movement speed
    this.canCheckCollision = false;
  }

  // Initialization method for setting up event listeners and initial state
  init(gui) {
    this.canMove = true;
    this.excitementObj = { value: 0 };
    this.isCollidedOnWall = false;
    this.gui = gui;
    this.mixer = null;
    this.isGhost = false;
    this.animationList = [];
    // State flags for smooth animation switching
    this.isWalking = false;
    this.isRunning = false;
    this.isIdle = false;
    this.isCollided = false;
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);

    // setInterval(() => {
    //   this.isCollided = true;
    //   this.showCollision('F');
    // }, 4000);
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'add_player':
        this.addPlayer();
        break;
      case 'update':
        this.update(data['message']['data']);
        break;

      // Event listener for input state from GameControl
      case 'PLAYER_INPUT_UPDATE':
        this.handleInputUpdate(data['message']['data'].inputState);
        break;

      // Handle momentary swipes (for touch turning)
      case 'on_swipe':
        this.handleSwipe(data['message']['data'].direction);
        break;

      case 'start_game':
        this.showPin();
        break;
      case 'add_pin':
        this.addPin(data['message']['data']);
        break;
      case 'game_over':
        this.canMove = false;
        this.doIdle(true);
        break;
      case 'replay_game':
        this.onReplay();
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  // Checks for movement and switches between Walk and Idle
  handleInputUpdate(newInputState) {
    this.input = newInputState;

    const { forward, left, right } = this.input;
    const isMoving = (forward || left || right) && !this.isCollided;

    if (isMoving) {
      this.doWalk();
    } else {
      this.doIdle();
    }
  }

  // Handle momentary swipes (for touch turning logic)
  handleSwipe(direction) {
    // Swipes provide a momentary nudge to the target rotation
    const rotationNudge = Math.PI / 8; // 22.5 degrees turn
    if (direction === 'Left') {
      this.targetRotationY += rotationNudge;
    } else if (direction === 'Right') {
      this.targetRotationY -= rotationNudge;
    }
  }

  async addPin(data) {
    this.profileAsset = Global.assets['textures'][`profile`]['asset'];
    if (Global.profileUrl != null) {
      const textLoader = new THREE.TextureLoader();
      const proxyUrl = `https://www.newsfeedsmartapp.com/pringles/api/proxy.php?url=${encodeURIComponent(Global.profileUrl)}`;
      const userImg = await textLoader.loadAsync(proxyUrl);
      this.profileAsset = userImg;

      userImg.wrapS = THREE.ClampToEdgeWrapping; // Prevents horizontal repetition
      userImg.wrapT = THREE.ClampToEdgeWrapping; // Prevents vertical repetition
      userImg.repeat.set(1, 1); // Ensures it only shows once
    }
    this.pin = Global.assets['glbs'][`pin`]['asset'].scene;
    //.clone();
    this.pin.scale.set(0, 0, 0);
    this.scene.add(this.pin);
    this.pin.traverse((item) => {
      if (!item.isMesh) return;

      if (item.name == 'Pin_(1)001_1') {
        item.material = new THREE.MeshStandardMaterial({
          map: this.profileAsset,
          transparent: true,
          envMapIntensity: Math.min(1, Global.envIntensity),
        });
        item.material.map.flipY = false;
        item.material.map.colorSpace = THREE.SRGBColorSpace;
        item.material.map.encoding = THREE.sRGBEncoding;
      }
    });
  }
  onReplay() {
    this.player.position.set(-0.7, 0.2, 10);
  }
  addPlayer() {
    this.player = Global.assets['glbs'][`${Global.gender}`]['asset'].scene; //.clone();
    this.scene.add(this.player);
    Global.player = this.player;

    // this.player.position.set(0, 0.2, 10); // Y=2 so it stands on ground (y=0)
    if (Global.gameTry == 1) {
      this.player.position.set(36.5187, 15.4811, -60.2515);
      setTimeout(() => {
        this.player.position.set(-0.7, 0.2, 10);
      }, 1000);
    } else {
      this.player.position.set(-0.7, 0.2, 10);
      Global.playerPos = this.player.position.clone();
      Global.playerQuot = this.player.quaternion.clone();
      // .addScaledVector(new THREE.Vector3(0, -10, 0), 1);
    }

    // this.player.position.set(30, 0, -30);
    this.player.rotation.y = Math.PI; // Face forward initially (down the -Z axis)

    this.player.userData.itemType = 'player';
    // this.player.visible = false;

    this.player.traverse((item) => {
      if (item.isMesh) {
        if (item.name.includes('HEAD')) {
          item.visible = false;
        }
        if (item.isMesh && item.material) {
          item.material.opacity = 1;
        }
        item.material.envMapIntensity = Math.max(1.25, Global.envIntensity);

        item.castShadow = true;
        item.material.transparent = true;
        if (item.material && item.material.map) {
          item.material.map.colorSpace = THREE.SRGBColorSpace;
          item.material.map.encoding = THREE.sRGBEncoding;
        }
      }
    });

    Global.assets['textures'][`profile`]['asset'].encoding = THREE.sRGBEncoding;
    Global.assets['textures'][`profile`]['asset'].colorSpace =
      THREE.SRGBColorSpace;

    this.targetRotationY = this.player.rotation.y;

    this.mixer = new THREE.AnimationMixer(this.player);
    this.walk = this.mixer.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`${Global.gender}`]['asset']['animations'],
        'RUN slow '
      )
    );
    this.idle = this.mixer.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`${Global.gender}`]['asset']['animations'],
        'idle'
      )
    );
    this.hitF = this.mixer.clipAction(
      THREE.AnimationClip.findByName(
        Global.assets['glbs'][`${Global.gender}`]['asset']['animations'],
        'bumping 01'
      )
    );

    this.hitF.setLoop(THREE.LoopRepeat, 1);
    this.hitF.clampWhenFinished = true;

    // Play both animations initially and fade out walk, so idle is the visible state.
    this.idle.play();
    this.walk.play();
    this.hitF.play();
    this.walk.fadeOut(0);
    this.hitF.fadeOut(0);

    // Ensure both are enabled, then set the initial weights via fade
    this.idle.enabled = true;
    this.walk.enabled = true;
    this.hitF.enabled = true;

    // Set initial state: fully idle, walk completely faded out.
    // this.idle.setEffectiveWeight(1);
    // this.walk.setEffectiveWeight(0);
    this.isIdle = true; // Set state flag manually since we aren't calling doIdle()
  }

  /**
   * 🆕 Raycasting logic to check for obstacles in the forward direction.
   */
  showPin() {
    // this.player.visible = true;
    // Global.soundEnabled && document.querySelector(`#collect1Audio`).play();
    gsap.to(this.pin.scale, {
      x: 0.5,
      y: 0.5,
      z: 0.5,
      duration: 0.4,
      ease: 'bounce.out',
    });
    // gsap.to(this.pin.position, {
    //   y: 0.5,
    //   y: 0.5,
    //   z: 0.5,
    //   duration: 0.5,
    //   delay: 1,
    //   ease: 'back.out',
    // });
    //this.pin
  }
  checkCollision() {
    // If the player is currently in a blinking/ghost state, ignore collisions
    // if (this.player) {
    //   return false;
    // }
    if (!this.player || !this.player.parent) return false;

    // 1. Get the player's world position (origin of the ray)
    // Lift the origin slightly to prevent intersecting the ground plane (y=0)
    const origin = this.player.position.clone();
    origin.y += 0.5;

    // 2. Get the player's forward direction (direction of the ray)
    const direction = new THREE.Vector3(0, 0, 1);
    direction.applyQuaternion(this.player.quaternion);
    direction.normalize();

    // const direction2 = new THREE.Vector3(0.25, 0, 1);
    // direction2.applyQuaternion(this.player.quaternion);
    // direction2.normalize();

    // const direction3 = new THREE.Vector3(-0.25, 0, 1);
    // direction3.applyQuaternion(this.player.quaternion);
    // direction3.normalize();

    const direction4 = new THREE.Vector3(0, -1, 0);
    direction4.applyQuaternion(this.player.quaternion);
    direction4.normalize();

    // 3. Cast the ray
    this.raycaster.set(origin, direction);
    this.raycaster.far = this.collisionDistance;

    // Perform the intersection test (recursive: true checks children meshes)
    const carsToCheck = this.isGhost ? [] : Global.cars;

    const collidableObjects = [
      ...carsToCheck,
      Global.wallCollider1,
      Global.wallCollider2,
    ].filter((object) => object != null); // or object !== null && object !== undefined

    const intersects = this.raycaster.intersectObjects(
      collidableObjects,
      false
    );

    this.raycaster.set(origin, direction4);
    const roadIntersection = this.raycaster.intersectObjects(
      Global.roadMeshes,
      false
    );
    if (roadIntersection.length > 0) {
      this.player.position.y = THREE.MathUtils.lerp(
        this.player.position.y,
        roadIntersection[0].point.y - 0.01,
        Global.delta * 0.1
      );
    }

    // Check if an intersection was found within the collision distance
    let wasCollided = intersects.length > 0;

    /*  if (wasCollided) {
      if (this.isGhost) {
        wasCollided = false;
      }
    } else */ if (!wasCollided && this.isMoving) {
      this.isCollidedOnWall = false;
    }
    // wasCollided = false;
    if (wasCollided && !this.isCollided) {
      // Update the collision state
      // 💥 Transition to collision state
      this.isCollidedOnWall =
        intersects[0].object == Global.wallCollider1 ||
        intersects[0].object == Global.wallCollider2;
      if (!this.isCollidedOnWall && this.isGhost) return false;

      ((this.isCollided = true), this.showCollision()); // Trigger the 'hit' animation
      // this.notifyBlink(intersects[0].object);

      if (!this.isCollidedOnWall) {
        let targetObject = null;
        let currentObject = intersects[0].object;

        // Traverse up the hierarchy until the object has the 'selectable' property
        while (currentObject) {
          if (
            currentObject.userData &&
            currentObject.userData.selectable === true
          ) {
            targetObject = currentObject;
            break; // Found the intended parent, stop the loop
          }
          if (currentObject.parent.isScene) {
            break;
          }
          // Move to the next parent up the hierarchy
          currentObject = currentObject.parent;

          // Optional: Stop traversing once you hit the Scene object
        }
        if (currentObject) {
          this.events.dispatchEvent({
            type: 'GAME_EVENTS',
            message: {
              event_type: 'car_stop_and_proceed',
              data: {
                uuid: currentObject.uuid,
              },
            },
          });
        }
      }
    }
    let intersects2 = [];
    // 3. Loop through your objects
    Global.pringlesToCollide.forEach((object) => {
      // Ensure the object's matrix is up to date
      object.updateMatrixWorld();

      // Get the object's world position
      const objectPosition = new THREE.Vector3();
      object.getWorldPosition(objectPosition);

      // Calculate distance
      const dist = origin.distanceTo(objectPosition);

      if (dist <= (isMobile ? 1.8 : 1.2)) {
        // Mimic the raycaster's return structure if needed
        intersects2.push({
          distance: dist,
          object: object,
          point: objectPosition,
        });
      }
    });
    if (intersects2.length > 0) {
      this._blinkTween && this._blinkTween.kill();
      this.excitementObj.value = 0;
      this._blinkTween = gsap.to(this.excitementObj, {
        value: 0.05,
        duration: 0.1,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: 1,
      });

      this.events.dispatchEvent({
        type: 'GAME_EVENTS',
        message: {
          event_type: 'pringle_collected',
          data: {
            uuid: intersects2[0].object.uuid,
          },
        },
      });
    }

    // const origin2 = this.player.position.clone();
    // origin2.y = 1;
    // this.raycaster.set(origin2, direction);
    // this.raycaster.far = this.collisionDistance * 2 * (isMobile ? 1.5 : 1);
    // let intersects2 = []; //this.raycaster.intersectObjects(Global.pringles, true);

    // let pringleCollected = intersects2.length > 0;

    // this.raycaster.set(origin2, direction2);
    // intersects2 = this.raycaster.intersectObjects(
    //   Global.pringlesToCollide,
    //   false
    // );
    // pringleCollected = intersects2.length > 0;

    // if (!pringleCollected) {
    // }
    // if (!pringleCollected) {
    //   this.raycaster.set(origin2, direction3);
    //   intersects2 = this.raycaster.intersectObjects(
    //     Global.pringlesToCollide,
    //     false
    //   );
    //   pringleCollected = intersects2.length > 0;
    // }
    // if (pringleCollected) {
    //   let targetObject = null;
    //   let currentObject = intersects2[0].object;

    //   // Traverse up the hierarchy until the object has the 'selectable' property
    //   while (currentObject) {
    //     if (
    //       currentObject.userData &&
    //       currentObject.userData.selectable === true
    //     ) {
    //       targetObject = currentObject;
    //       break; // Found the intended parent, stop the loop
    //     }
    //     if (currentObject.parent.isScene) {
    //       break;
    //     }
    //     // Move to the next parent up the hierarchy
    //     currentObject = currentObject.parent;

    //     // Optional: Stop traversing once you hit the Scene object
    //   }

    //   if (currentObject) {
    //     this._blinkTween && this._blinkTween.kill();
    //     this.excitementObj.value = 0;
    //     this._blinkTween = gsap.to(this.excitementObj, {
    //       value: 0.05,
    //       duration: 0.1,
    //       ease: 'sine.inOut',
    //       yoyo: true,
    //       repeat: 1,
    //     });

    //     this.events.dispatchEvent({
    //       type: 'GAME_EVENTS',
    //       message: {
    //         event_type: 'pringle_collected',
    //         data: {
    //           uuid: currentObject.uuid,
    //         },
    //       },
    //     });
    //   }
    // }

    // return this.isCollided;
  }

  notifyBlink(object) {
    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'collide_on_vehicle',
        data: {
          uuid: object.uuid,
        },
      },
    });
  }
  // Uses fadeIn/fadeOut exactly as requested
  showCollision() {
    if (!this.isCollided) return;

    this.doIdle();
    // this.isWalking && this.walk.fadeOut(this.isCollidedOnWall ? 0.1 : 0.5);

    if (this._isBlinking) return;
    this._isBlinking = true;

    // if (!this.isCollidedOnWall) {

    // }

    if (!this.isCollidedOnWall) {
      this.blinkData = { value: 0 };
      this.blinkTwn = gsap.to(this.blinkData, {
        value: 1,
        duration: 0.18,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: 20,
        onUpdate: function () {
          this.player.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.opacity = this.blinkData.value;
            }
          });
        }.bind(this),
        onComplete: function () {
          this.isGhost = false;
        }.bind(this),
      });
      this.isGhost = true;
    }
    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'shake_camera',
        data: {
          shake_fact: this.isCollidedOnWall ? 0.03 : 0.1,
          shake_time: this.isCollidedOnWall ? 0.01 : 0.3,
        },
      },
    });

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'hit_sound',
        data: {
          isWall: this.isCollidedOnWall,
        },
      },
    });
    // Ensure the hit action starts from beginning and plays once
    this.hitF.reset();
    this.hitF.setLoop(THREE.LoopOnce, 1);
    this.hitF.clampWhenFinished = true;
    this.hitF.enabled = true;
    this.hitF.fadeIn(this.isCollidedOnWall ? 0.3 : 0.55);
    this.hitF.play();

    let impactObj = {
      value: 0,
    };
    let lastIpact = 0;

    this.impactTwn = gsap.to(impactObj, {
      value: 1,
      duration: this.isCollidedOnWall ? 0.4 : 0.5,
      // ease: 'power2.out',
      yoyo: false,
      repeat: 0,
      onUpdate: (a, b) => {
        const combinedMovementVector = new THREE.Vector3(0, 0, 0);
        combinedMovementVector.z = -1 / Global.delta;

        let effectiveSpeed =
          PLAYER_SPEED *
          Global.delta *
          (impactObj.value - lastIpact) *
          (!this.isCollidedOnWall ? 10 : 5);

        // Apply player's current rotation to the movement vector to get world direction
        combinedMovementVector.applyEuler(this.player.rotation);

        // Update player position (position += direction * speed * deltaTime)
        this.player.position.addScaledVector(
          combinedMovementVector,
          effectiveSpeed /*  * Global.delta */
        );
        lastIpact = impactObj.value;
      },
      onComplete: function () {
        this.healTO = setTimeout(
          () => {
            // this.isGhost = false;
            this._isBlinking = false;
            this._blinkTimeoutId = null;
            this.doIdle(true);
            this.handleInputUpdate(this.input);
          },
          this.isCollidedOnWall ? 100 : 100
        );
      }.bind(this),
    });

    return;

    // Start smooth blinking on the player (move blink responsibility from Car to Player)
    // Prevent overlapping blinks
    if (this._isBlinking) return;
    this._isBlinking = true;

    // Clear any existing blink tween/timeout
    if (this._blinkTween) {
      try {
        this._blinkTween.kill();
      } catch (e) {}
      this._blinkTween = null;
    }
    if (this._blinkTimeoutId) {
      clearTimeout(this._blinkTimeoutId);
      this._blinkTimeoutId = null;
    }

    // Ensure player.userData exists
    // if (!this.player.userData) this.player.userData = {};
    this.isGhost = true;

    // Collect unique materials and store original states (dedupe by UUID)
    // We will pulse emissive color/intensity instead of changing opacity to
    // avoid revealing the skeleton when the mesh becomes transparent.
    const matMap = new Map();
    this.player.traverse((node) => {
      if (node.isMesh && node.material) {
        const mats = Array.isArray(node.material)
          ? node.material
          : [node.material];
        mats.forEach((m) => {
          if (!m || matMap.has(m.uuid)) return;
          matMap.set(m.uuid, {
            material: m,
            originalEmissive: m.emissive ? m.emissive.clone() : null,
            originalEmissiveIntensity:
              m.emissiveIntensity !== undefined ? m.emissiveIntensity : null,
            originalColor: m.color ? m.color.clone() : null,
          });
          // mark for update if we change properties
          m.needsUpdate = true;
        });
      }
    });
    const matRecords = Array.from(matMap.values());

    const proxy = { v: 1 };
    const glowColor = new THREE.Color(0xff0000); // soft red glow
    const glowIntensity = 0.15; // multiplier for emissive intensity (if available)
    this._blinkTween = gsap.to(proxy, {
      v: 0,
      duration: 0.4,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        const t = 1 - proxy.v; // 0..1 where 1 is full glow
        matRecords.forEach((rec) => {
          const m = rec.material;
          if (!m) return;
          // Prefer emissive changes where supported
          if (m.emissive) {
            if (rec.originalEmissive) {
              // lerp emissive color toward glowColor
              m.emissive.copy(rec.originalEmissive).lerp(glowColor, t);
            } else {
              m.emissive.lerp(glowColor, t);
            }
            // adjust emissiveIntensity if available
            if (
              rec.originalEmissiveIntensity !== null &&
              rec.originalEmissiveIntensity !== undefined
            ) {
              m.emissiveIntensity =
                rec.originalEmissiveIntensity * (1 + (glowIntensity - 1) * t);
            }
          } else if (m.color && rec.originalColor) {
            // fallback: slightly brighten the base color toward white/glow
            m.color.copy(rec.originalColor).lerp(glowColor, t * 0.5);
          }
          // ensure material updates
          m.needsUpdate = true;
        });
      },
    });

    // Stop blinking after 3 seconds and restore materials/userData
    this._blinkTimeoutId = setTimeout(() => {
      if (this._blinkTween) {
        try {
          this._blinkTween.kill();
        } catch (e) {}
        this._blinkTween = null;
      }
      matRecords.forEach((rec) => {
        if (!rec.material) return;
        const m = rec.material;
        if (m.emissive && rec.originalEmissive) {
          m.emissive.copy(rec.originalEmissive);
        }
        if (
          rec.originalEmissiveIntensity !== null &&
          rec.originalEmissiveIntensity !== undefined
        ) {
          m.emissiveIntensity = rec.originalEmissiveIntensity;
        }
        if (m.color && rec.originalColor) {
          m.color.copy(rec.originalColor);
        }
        m.needsUpdate = true;
      });

      this.isGhost = false;
      this._isBlinking = false;
      this._blinkTimeoutId = null;
      this.doIdle(true);
      this.handleInputUpdate(this.input);
    }, 1000);
  }

  doIdle(byForce = false) {
    if (this.isIdle && !byForce) return; // Already idle, do nothing

    this.walkTO && clearTimeout(this.walkTO);
    // Fade out the walking animation and fade in the idle animation
    if (!this.isIdle) {
      this.hitF && (this.hitF.enabled = false);
      this.walk.enabled && this.walk.fadeOut(0.25);
      this.idleTO = setTimeout(
        function (isWalking) {
          // this.walk.fadeOut(0.1);
          this.idle.enabled = true;
          this.idle.fadeIn(0.3);
        }.bind(this, this.isWalking),
        50
      ); // Slightly longer fade-in duration can sometimes look smoother
      this.isWalking = false;
      // Update state flags
      this.isIdle = true;

      this.events.dispatchEvent({
        type: 'GAME_EVENTS',
        message: {
          event_type: 'player_is_idle',
          data: {},
        },
      });
    }

    if (byForce) this.isCollided = false;
  }

  // Uses fadeIn/fadeOut exactly as requested
  doWalk() {
    if (!this.canMove) return;
    if (this.isWalking) return; // Already walking, do nothing

    // Fade out the idle animation and fade in the walking animation
    this.idleTO && clearTimeout(this.idleTO);

    this.idle.enabled && this.idle.fadeOut(0.5);
    this.hitF.enabled && this.hitF.fadeOut(0.5);
    this.isIdle = false;
    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'player_is_move',
        data: {},
      },
    });
    this.walkTO = setTimeout(
      function () {
        this.isIdle = false;
        // this.walk.fadeOut(0.1);
        this.walk.enabled = true;
        this.walk.fadeIn(0.55);

        this.isWalking = true;
      }.bind(this, this.isWalking),
      50
    );
    // Update state flags
  }

  // Main update loop
  update(data) {
    if (!this.player) return;

    Global.playerPos = this.player.position.clone();
    Global.playerQuot = this.player.quaternion.clone();

    // 🆕 Check for collisions before calculating movement
    const deltaTime = data.deltaTime || 1 / 60;
    if (Global.isGameActive) {
      this.canCheckCollision = !this.canCheckCollision;
      this.canCheckCollision && this.checkCollision();
      // ------------------------------------------

      const { forward, left, right } = this.input;
      const isMoving = forward || left || right;

      Global.isForward = forward;

      // --- 1. Rotation Logic ---
      const rotationFactor =
        (forward ? (isMobile ? 0.5 : 0.45) : 0.75) * Global.rotateFact;
      const currentRotationSpeed =
        ROTATION_SPEED * rotationFactor * Global.delta * (forward ? 0.8 : 0.5);

      if (left) {
        this.targetRotationY += currentRotationSpeed;
      }
      if (right) {
        this.targetRotationY -= currentRotationSpeed;
      }

      this.player.rotation.y = this.lerpAngle(
        this.player.rotation.y,
        this.targetRotationY,
        SNAP_ROTATION_FACTOR * (this.input.isJoyStick ? 10 : 1)
      );

      // --- 2. Movement Logic (with Speed Lerp) ---

      // 2a. Determine the target speed (full speed, half speed, or zero)
      let targetSpeed = 0;
      // 🛑 Movement is blocked if collision is active

      if (isMoving && !this.isCollided) {
        targetSpeed = PLAYER_SPEED * Global.delta;
        // If only turning keys are pressed (A/D) but not forward (W), halve the speed
        if (!forward && (left || right)) {
          targetSpeed = PLAYER_SPEED * 0.5 * Global.delta;
        }
      }

      // 2b. Smoothly interpolate the actual speed towards the target speed
      // console.log(1 - Math.min(1, Global.rotateFact / 4));

      this.currentSpeed = THREE.MathUtils.lerp(
        this.currentSpeed,
        targetSpeed *
          (forward
            ? this.input.isJoystickActive /* && Global.rotateFact > 0.75 */
              ? 1 - Math.min(1, Global.rotateFact / 3)
              : 1
            : 0.85) *
          (this.input.isJoystickActive ? 1 : 1),
        this.isCollided
          ? 1
          : isMoving
            ? SPEED_LERP_FACTOR * Global.delta
            : SPEED_LERP_FACTOR * 10 * Global.delta
      );

      if (
        this.currentSpeed >
        0.01 * Global.delta /* && !this.isCollidedOnWall */
      ) {
        const combinedMovementVector = new THREE.Vector3(0, 0, 0);
        combinedMovementVector.z = 1 / Global.delta;

        let effectiveSpeed = this.currentSpeed;

        // Apply player's current rotation to the movement vector to get world direction
        combinedMovementVector.applyEuler(this.player.rotation);

        // Update player position (position += direction * speed * deltaTime)

        this.player.position.addScaledVector(
          combinedMovementVector,
          effectiveSpeed * Global.delta
        );
      }

      // --- 3. Kinematic Event Dispatch (For Camera) ---
    }
    const isCurrentlyMoving = this.currentSpeed > 0.1;
    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'kinematic_data',
        data: {
          position: this.player.position.clone(),
          rotationY: this.player.rotation.y,
          isMoving: isCurrentlyMoving,
        },
      },
    });
    if (this.mixer) {
      // Delta time should be in seconds for the mixer
      this.mixer.update(0.011 * Global.delta); // Preserving original 0.01 value
    }
    if (this.pin) {
      this.pin.position
        .copy(this.player.position.clone())
        .addScaledVector(
          new THREE.Vector3(0, 1.9, 0),
          1 + this.excitementObj.value
        );
      this.pin.lookAt(this.camera.position.clone());
    }
  }

  /**
   * Helper function to smoothly interpolate angular rotation, handling wrap-around.
   */
  lerpAngle(start, end, factor) {
    let diff = end - start;
    // Wrap the difference to [-PI, PI]
    if (diff > Math.PI) diff -= 2 * Math.PI;
    if (diff < -Math.PI) diff += 2 * Math.PI;
    return start + diff * factor;
  }

  dispose() {
    this.blinkTwn && this.blinkTwn.kill();
    this.impactTwn && this.impactTwn.kill();
    this._blinkTween && this._blinkTween.kill();

    this.healTO && clearTimeout(this.healTO);
    if (this.player) {
      this.player.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.player);
      this.player = null;
    }
    if (this.pin) {
      this.pin.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.pin);
      this.pin = null;
    }

    // Remove event listeners
    if (this.events) {
      this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
      this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
      this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    }

    if (window.gc) {
      window.gc();
    }
  }
}
